import { useCallback, useMemo, useState } from 'react';
// Относительный путь, а не alias: модуль без alias-импортов можно собрать в тест.
import { pageList, type PageWindow } from '../../../shared/lib/pager';
import type { LookupConstruction, LookupResult, LookupSense } from '../api/workspaceApi';
import { canonicalPos } from './pos';

/**
 * Выбор значений при создании карточек (макеты «Создание карточек v2», десктоп и мобайл,
 * 2026-10-06). Ответ поиска — четыре раздела: «Активно используются», «Другие и редкие»,
 * «Фразы», «Фразовые глаголы». Каждое отмеченное значение — своё значение карточки;
 * значения фразы учатся в карточке самой фразы (`query`), которая создаётся тем же нажатием.
 * Общая логика для десктопного спотлайта и мобильного экрана выбора.
 */

/** Сколько значений можно выбрать за одно нажатие — вместе со значениями фраз (решение
 *  владельца 2026-10-06; совпадает с MAX_PICKS_PER_CREATE на бэке). */
export const MAX_PICKS = 10;

/** Значение в выборе: к какой карточке оно относится (слово или фраза). */
export interface SenseItem extends LookupSense {
  /** Слово карточки: само слово или `query` фразы. */
  card: string;
}

export interface ConstructionItem extends Omit<LookupConstruction, 'senses'> {
  senses: SenseItem[];
}

export interface LookupView {
  word: string;
  source: LookupResult['source'];
  active: SenseItem[];
  other: SenseItem[];
  /** Фразы и фразовые глаголы для выбора — без редких. */
  phrases: ConstructionItem[];
  phrasalVerbs: ConstructionItem[];
  /** Редкие фразы и фразовые глаголы — в разделе «Редкие», только для чтения (решение
   *  владельца 2026-10-07): их не генерируют, как и редкие значения слова. */
  rarePhrases: ConstructionItem[];
  all: SenseItem[];
}

export function buildLookupView(data: LookupResult): LookupView {
  const word = data.frontName;
  const senses = (list: LookupSense[], card: string): SenseItem[] => list.map((s) => ({ ...s, card }));
  const cons = (list: LookupConstruction[]): ConstructionItem[] =>
    list.map((c) => ({ ...c, senses: senses(c.senses, c.query) }));
  const phrases = cons(data.phrases);
  const phrasalVerbs = cons(data.phrasalVerbs);
  const view = {
    word,
    source: data.source,
    active: senses(data.active, word),
    other: senses(data.other, word),
    phrases: phrases.filter((c) => !isRare(c)),
    phrasalVerbs: phrasalVerbs.filter((c) => !isRare(c)),
    rarePhrases: [...phrases, ...phrasalVerbs].filter(isRare),
  };
  return {
    ...view,
    all: [
      ...view.active,
      ...view.other,
      ...phrases.flatMap((c) => c.senses),
      ...phrasalVerbs.flatMap((c) => c.senses),
    ],
  };
}

export const isLocked = (x: SenseItem) => x.learned || x.generating;
export const isRare = (x: { frequency: string | null }) => x.frequency === 'rare';
/** Редкие значения слова, редкие фразы и фразовые глаголы — только для чтения (решение
 *  владельца 2026-10-07): модель их часто не знает (go = «обстоятельство» → «That is the go
 *  of work.»), карточка выходила без нормального предложения и перевода. Нужное редкое
 *  значение добавляют вручную. */
const readOnlyIds = (view: LookupView) =>
  new Set([...view.other.filter(isRare), ...view.rarePhrases.flatMap((c) => c.senses)].map((x) => x.id));

/** Фразы раскрываются порциями (решение владельца 2026-10-06): сначала 6, «ещё» добавляет
 *  по 10 (у go их 126 — разом открывать нельзя), «свернуть» возвращает как было. */
export const PHRASE_ROWS = 6;
export const PHRASE_STEP = 10;
export interface PhrasePager {
  limit: number;
}
export const PHRASE_PAGER: PhrasePager = { limit: PHRASE_ROWS };
const PHRASE_WINDOW: PageWindow = { base: PHRASE_ROWS, step: PHRASE_STEP };

/** Какие фразы раздела видны при этом состоянии. keep — строки, которые не прячем
 *  никогда (раскрытая или с отмеченным значением). Редких здесь нет — они в «Редких».
 *  Порции считает общий pageList (shared/lib/pager) — тот же, что в записях «Генераций». */
export function pagePhrases(list: ConstructionItem[], pager: PhrasePager, keep: (c: ConstructionItem) => boolean) {
  const page = pageList(list, pager.limit, PHRASE_WINDOW, keep);
  return {
    visible: page.visible,
    more: page.more,
    expand: (p: PhrasePager): PhrasePager => ({ ...p, limit: p.limit + PHRASE_STEP }),
    canCollapse: page.canCollapse,
  };
}

/** Значения одной части речи подряд, в порядке первого появления. */
export function groupByPos(list: SenseItem[]): { pos: string | null; items: SenseItem[] }[] {
  const out: { pos: string | null; items: SenseItem[] }[] = [];
  for (const x of list) {
    let g = out.find((k) => k.pos === x.pos);
    if (!g) out.push((g = { pos: x.pos, items: [] }));
    g.items.push(x);
  }
  return out;
}

/** По умолчанию — первое доступное активное значение; нет его — фраза с одним значением;
 *  иначе первое из «Других». Быстрый путь «слово → Enter» остаётся. */
function defaultSelection(view: LookupView): string[] {
  const ok = (x: SenseItem) => !isLocked(x);
  const a = view.active.find(ok);
  if (a) return [a.id];
  const c = [...view.phrases, ...view.phrasalVerbs].find((k) => k.senses.length === 1 && ok(k.senses[0]));
  if (c) return [c.senses[0].id];
  const o = view.other.find((x) => ok(x) && !isRare(x));
  return o ? [o.id] : [];
}

/** Будущая карточка: слово или фраза и её выбранные значения. */
export interface ChoiceCard {
  front: string;
  isWord: boolean;
  translations: string[];
  picks: { pos: string | null; translation: string }[];
}

export type ToggleResult = 'on' | 'off' | 'limit' | 'locked';

/**
 * «Ввести снова» из записи «Недавно добавленных» (ТЗ главной § 22.6): какие значения отметить в листе.
 * Привязаны к слову поиска — применяются к ответу, у которого `frontName` совпадает с `word`; на другом
 * слове не применяются.
 */
export interface InitialPicks {
  word: string;
  /** Слово или фраза карточки (у фразы — сама фраза), часть речи и главный перевод. */
  picks: readonly { front: string; pos: string | null; translation: string }[];
}

const normText = (text: string) => text.trim().toLowerCase();

/**
 * Значения ответа, совпавшие с недобравшимися: слово карточки (`card`, у фразы — `query`), каноническая
 * часть речи и главный перевод (без регистра и пробелов по краям). Не нашлось, уже «учишь», создаётся или
 * только для чтения — не отмечается; остальные отмечены (не больше MAX_PICKS).
 */
export function matchInitialPicks(view: LookupView, initial: InitialPicks): string[] {
  const readOnly = readOnlyIds(view);
  const ids: string[] = [];
  for (const pick of initial.picks) {
    const found = view.all.find(
      (x) =>
        normText(x.card) === normText(pick.front) &&
        canonicalPos(x.pos) === canonicalPos(pick.pos) &&
        normText(x.main) === normText(pick.translation),
    );
    if (!found || isLocked(found) || readOnly.has(found.id) || ids.includes(found.id)) continue;
    ids.push(found.id);
    if (ids.length >= MAX_PICKS) break;
  }
  return ids;
}

/** Пустой список отметок «Ввести снова» — один и тот же объект: по его смене экраны выбора понимают, что пора решать. */
const NO_INITIAL_IDS: readonly string[] = Object.freeze([]);

/** Отметки «Ввести снова», применённые к ответу на слово (ТЗ главной § 23.6). */
export interface AppliedInitialPicks {
  source: InitialPicks;
  word: string;
  ids: string[];
}

/**
 * Применение `initialPicks` к ответу — один раз на пару «отметки — слово»: тихое обновление того же ответа
 * (уведомление, смена модуля на этапе 2) его не пересматривает. Пара та же или на экране ответ на другое слово
 * (прошлый, пока ищется новое) — прежний объект; отметок нет — null.
 */
export function nextAppliedInitial(
  prev: AppliedInitialPicks | null,
  initial: InitialPicks | null,
  view: LookupView | null,
): AppliedInitialPicks | null {
  if (!initial) return null;
  if (!view || normText(initial.word) !== normText(view.word)) return prev;
  if (prev && prev.source === initial && prev.word === view.word) return prev;
  return { source: initial, word: view.word, ids: matchInitialPicks(view, initial) };
}

/** Значения, которые «Ввести снова» отметил в этом ответе; ответ не на это слово или отметок нет — общий пустой список. */
export function appliedInitialIds(
  applied: AppliedInitialPicks | null,
  initial: InitialPicks | null,
  view: LookupView | null,
): readonly string[] {
  return applied && initial && view && applied.source === initial && applied.word === view.word ? applied.ids : NO_INITIAL_IDS;
}

/**
 * Какие свёрнутые по умолчанию разделы открыть при показе ответа (ТЗ главной § 23.6): где лежит значение, которое
 * отметил «Ввести снова» и которое всё ещё отмечено. «Другие» — в листе компьютера и на этапе 2 телефона, «Фразы» и
 * «Фразовые глаголы» — на этапе 2 (в листе они и так держат отмеченные строки в порции). Редкие отметить нельзя.
 */
export function sectionsToOpen(
  view: LookupView,
  ids: readonly string[],
  isSelected: (id: string) => boolean,
): { other: boolean; phrases: boolean; phrasalVerbs: boolean } {
  const marked = new Set(ids.filter(isSelected));
  const has = (list: SenseItem[]) => marked.size > 0 && list.some((x) => marked.has(x.id));
  return {
    other: has(view.other.filter((x) => !isRare(x))),
    phrases: view.phrases.some((c) => has(c.senses)),
    phrasalVerbs: view.phrasalVerbs.some((c) => has(c.senses)),
  };
}

export function useSenseChoice(data: LookupResult | null, options: { initialPicks?: InitialPicks | null } = {}) {
  const view = useMemo(() => (data ? buildLookupView(data) : null), [data]);
  const initial = options.initialPicks ?? null;
  const [picked, setPicked] = useState<{ word: string | null; ids: string[] }>({ word: null, ids: [] });
  // Пришли новые значения для «Ввести снова» — прежний выбор того же слова не держим.
  const [initialSource, setInitialSource] = useState<InitialPicks | null>(initial);
  if (initialSource !== initial) {
    setInitialSource(initial);
    if (initial) setPicked({ word: null, ids: [] });
  }
  // Что «Ввести снова» отметил в ответе на своё слово — по этому экраны выбора один раз открывают свёрнутые разделы
  // (ТЗ главной § 23.6). Запоминается при первом применении и до новых отметок или другого слова не меняется.
  const [applied, setApplied] = useState<AppliedInitialPicks | null>(null);
  const nextApplied = nextAppliedInitial(applied, initial, view);
  if (nextApplied !== applied) setApplied(nextApplied);
  const initialIds = appliedInitialIds(nextApplied, initial, view);
  // Выбор нового слова: недобравшиеся значения «Ввести снова» (только на своём слове), иначе по
  // умолчанию.
  const seed = useCallback(
    (v: LookupView) => (initial && normText(initial.word) === normText(v.word) ? matchInitialPicks(v, initial) : defaultSelection(v)),
    [initial],
  );
  // Новое слово — выбор по умолчанию (без эффекта: сбрасываем при смене слова). Свежий ответ
  // по тому же слову (тихое обновление после уведомления) выбор сохраняет: ставшее «учишь» /
  // «создаётся» отсеется ниже.
  const same = view != null && picked.word === view.word;
  const ids = same ? picked.ids : view ? seed(view) : [];
  const byId = useMemo(() => new Map((view?.all ?? []).map((x) => [x.id, x])), [view]);
  const readOnly = useMemo(() => (view ? readOnlyIds(view) : new Set<string>()), [view]);
  const selected = ids.filter((id) => {
    const x = byId.get(id);
    return x != null && !isLocked(x) && !readOnly.has(id);
  });
  const atLimit = selected.length >= MAX_PICKS;

  const setIds = useCallback(
    (update: (prev: string[]) => string[]) =>
      setPicked((prev) => {
        if (!view) return prev;
        const base = prev.word === view.word ? prev.ids : seed(view);
        return { word: view.word, ids: update(base) };
      }),
    [view, seed],
  );

  const toggle = (id: string): ToggleResult => {
    const x = byId.get(id);
    if (!x || isLocked(x) || readOnly.has(id)) return 'locked';
    if (selected.includes(id)) {
      setIds((prev) => prev.filter((k) => k !== id));
      return 'off';
    }
    if (atLimit) return 'limit';
    setIds((prev) => [...prev, id]);
    return 'on';
  };

  const picks = selected.map((id) => byId.get(id)!).filter(Boolean);
  // Карточки в порядке выбора: слово и каждая фраза — отдельная карточка.
  const cards: ChoiceCard[] = [];
  for (const p of picks) {
    let c = cards.find((k) => k.front === p.card);
    if (!c) cards.push((c = { front: p.card, isWord: p.card === view?.word, translations: [], picks: [] }));
    c.translations.push(p.main);
    c.picks.push({ pos: p.pos, translation: p.main });
  }
  const allLocked = view != null && view.all.length > 0 && view.all.every(isLocked);

  return {
    view,
    selected,
    isSelected: (id: string) => selected.includes(id),
    toggle,
    picks,
    cards,
    count: picks.length,
    limit: MAX_PICKS,
    atLimit,
    allLocked,
    /** Хоть одно значение без готовой карточки — нажатие спишет генерацию. */
    needsGeneration: picks.some((p) => p.ready !== true),
    /** «Ввести снова» (ТЗ главной § 23.6): значения, отмеченные из `initialPicks` на этом слове. Тот же массив, пока
     *  не придут новые отметки или другое слово: по его смене экраны выбора решают открытость разделов. */
    initialIds,
  };
}

export type SenseChoice = ReturnType<typeof useSenseChoice>;
